import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Project, Board, Change } from '../../models';
import { RootState, useAppSelector, useAppDispatch } from '../../redux';
import { moveChange, loadProjects, loadBoards, loadBoard } from '../../redux';
import { SelectField, Button, Icon } from '../../components';
import {
    prefersListLanding,
    placeableBoardsOf,
    placeableColumnsOf,
    shouldShowBoardPicker,
    shouldShowColumnPicker,
    PlaceableBoard,
} from '../../utils/moveChangePlacement';

interface MoveChangeModalProps {
    show: boolean;
    initialBoardId: string;
    onClose?: () => void;
    onMoved?: (change: Change) => void;
}

function MoveChangeModal(props: MoveChangeModalProps) {
    const { onClose } = props;

    const { t }                 = useTranslation();
    const { project, projects } = useAppSelector((state : RootState) => state.projects);

    const { boards }                       = useAppSelector((state : RootState) => state.boards);
    const [projectId, setProjectId]        = useState(project?._id || '');
    const [boardId, setBoardId]            = useState('');
    const [columnId, setColumnId]          = useState('');
    const [selectedProject, selectProject] = useState<Project | undefined>(project);

    const {
        movingChange, moveChangeError, change
    } = useAppSelector((state : RootState) => state.changes);

    const dispatch = useAppDispatch();
    const prefersList = prefersListLanding(selectedProject);
    const placeableBoards = React.useMemo(
        () => placeableBoardsOf(selectedProject),
        [selectedProject]
    );

    useEffect(()=>{
        dispatch(loadProjects());
    },[dispatch]);

    useEffect(() => {
        if (boardId)
            dispatch(loadBoards({project: projectId}));
    }, [dispatch, boardId, projectId]);

    // List landing: no board picker — always the first placeable board.
    useEffect(() => {
        if (!prefersList || !selectedProject)
            return;

        const firstBoardId = placeableBoards[0]?._id || '';
        if (!firstBoardId) {
            if (boardId)
                setBoardId('');
            if (columnId)
                setColumnId('');
            return;
        }

        if (boardId !== firstBoardId) {
            setBoardId(firstBoardId);
            setColumnId('');
        }
    }, [prefersList, selectedProject, placeableBoards, boardId, columnId]);

    const board = React.useMemo(() => {
        if (!boardId)
            return null;

        const fromStore = boards?.find((item: Board) => item._id === boardId);
        if (fromStore)
            return fromStore;

        return placeableBoards.find((item: PlaceableBoard) => item._id === boardId) || null;
    }, [boardId, boards, placeableBoards]);

    const columnOptions = React.useMemo(() => {
        return placeableColumnsOf(board)
            .filter((column): column is AnyObj & { _id: string } => !!column._id)
            .map((column) => ({
                value: column._id,
                label: column.title
            }));
    }, [board]);

    // List landing: auto-pick the only placeable column.
    useEffect(() => {
        if (!prefersList || !boardId)
            return;

        if (columnOptions.length === 1 && columnId !== columnOptions[0].value) {
            setColumnId(columnOptions[0].value);
            return;
        }

        if (columnOptions.length === 0 && columnId)
            setColumnId('');
    }, [prefersList, boardId, columnOptions, columnId]);

    const onChangeProject = (_id: any) => {
        setProjectId(_id);
        setBoardId('');
        setColumnId('');

        if (!_id)
            selectProject(undefined);
        else
            selectProject(projects?.find((p: Project) => p._id === _id));
    };

    const onChangeBoard = (_id: string) => {
        setBoardId(_id);
        setColumnId('');
    };

    const navigate = useNavigate();
    const _moveChange = () => {
        const callback = (err: Error | null) => {
            if (err)
                return;

            dispatch(loadBoard(boardId));
            navigate(Project.path(selectedProject || projectId, 'change', change._id));
            onClose && onClose();
        }
        dispatch(moveChange(change._id, projectId, boardId, columnId, callback));
    }
    const close = () => {
        setProjectId('');
        setBoardId('');
        setColumnId('');
        navigate(Project.path(change.project, 'change', change._id));
        onClose && onClose();
    };

    const projectOptions = React.useMemo(() => {
        return projects?.map((project: Project) => ({
            value: project._id,
            label: project.name
        }));
    }, [projects]);

    const boardOptions = React.useMemo(() => {
        return placeableBoards
            .filter((board: PlaceableBoard): board is PlaceableBoard & { _id: string } => !!board._id)
            .map((board) => ({
                value: board._id,
                label: (typeof board.name === 'string' ? board.name : board.name?.fr) || ''
            }));
    }, [placeableBoards]);

    const showBoardPicker = shouldShowBoardPicker(selectedProject);
    const showColumnPicker = Boolean(
        board?._id === boardId &&
        shouldShowColumnPicker(selectedProject, boardId, columnOptions.length)
    );

    return (
        <div className="MoveChangeModal w-full rounded">
            <div className='bg-indigo-600 p-2 text-white rounded-t'>
                <h3 className="flex items-center space-x-2">
                    <Icon type="move" color="white" className="mr-1.5" />
                    {t('changes.move_this_change')}
                </h3>
            </div>
            <div className='h-full p-5'>
                {moveChangeError && (
                    <label className="block font-medium eading-6 text-white bg-red-600 text-xs rounded flex items-center p-2 mb-2">
                        <Icon type="warning" className="inline mr-1.5" color="white" />
                        {t('changes.could_not_move')}
                    </label>
                )}
                <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                    <Icon type="project" className="inline mr-1.5" />
                    {t('changes.into_the_project')}
                </label>
                <SelectField
                    name="projectId"
                    onChange={onChangeProject}
                    placeholder={t('changes.choose_a_project') || ''}
                    options={projectOptions}
                    optionsHeight="max-h-96"
                />
                {selectedProject && showBoardPicker && (
                    <div className='mt-5'>
                        {boardOptions.length > 0 ? (
                            <>
                                <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                    <Icon type="list" className="inline mr-1.5" />
                                    {t('changes.into_the_board')}
                                </label>
                                <SelectField
                                    name="boardId"
                                    onChange={onChangeBoard}
                                    placeholder={t('changes.choose_a_board') || ''}
                                    options={boardOptions}
                                    optionsHeight="max-h-96"
                                />
                            </>
                        ) : (
                            <span className="block text-sm italic font-bold text-center">
                                {t('changes.this_project_has_no_column')}
                            </span>
                        )}
                    </div>
                )}
                {selectedProject && prefersList && placeableBoards.length === 0 && (
                    <span className="mt-5 block text-sm italic font-bold text-center">
                        {t('changes.this_project_has_no_column')}
                    </span>
                )}
                {showColumnPicker && (
                    <div className='mt-5'>
                        {columnOptions.length > 0 ? (
                            <>
                                <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                    <Icon type="list" className="inline mr-1.5" />
                                    {t('changes.into_the_column')}
                                </label>
                                <SelectField
                                    name="columnId"
                                    onChange={setColumnId}
                                    placeholder={t('changes.choose_a_column') || ''}
                                    options={columnOptions}
                                    optionsHeight="max-h-96"
                                />
                            </>
                        ) : (
                            <span className="block text-sm italic font-bold text-center">
                                {board?.columns?.length > 0 ? t('changes.this_project_has_only_smart_columns') : t('changes.this_project_has_no_column')}
                            </span>
                        )}
                    </div>
                )}
                <div className='mt-5 flex justify-between items-center'>
                    <Button
                        small
                        onClick={close}
                        title={t('common.cancel')}
                        color="warning"
                        icon={movingChange === 'pending' ? 'loading' : 'close'}
                    />
                    {columnId && (
                        <Button
                            small
                            onClick={_moveChange}
                            title={t('common.move')}
                            color="primary"
                            icon={movingChange === 'pending' ? 'loading' : 'move'}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default MoveChangeModal;
